import { PublicClientApplication, type Configuration, type AccountInfo } from "@azure/msal-browser";

/**
 * Phase 15 D-AUTH-GATE-01 / D-OAUTH-02 — MSAL Browser config. Mirrors
 * cli/src/auth/msal.ts:79-111 (MSAL Node sibling) but uses auth-code+PKCE
 * via redirect rather than device flow (CLI uses device flow because no
 * browser; SPA has a browser).
 *
 * Env vars: VITE_OAUTH_CLIENT_ID + VITE_OAUTH_TENANT_ID are read at build
 * time by Vite (the VITE_ prefix is required for client-side exposure).
 * The same Entra App Registration as the CLI per OPERATOR-ONBOARDING.md.
 *
 * W-2 — Validation is LAZY (deferred to first call). Module load is
 * side-effect-free so CI (which has no Entra creds) can typecheck and
 * build the bundle without crashing. The throw only fires when the SPA
 * actually tries to authenticate at runtime.
 */
// SCOPES is computed lazily from VITE_OAUTH_CLIENT_ID via getScopes(). The
// access_token's `aud` claim must equal ENTRA_CLIENT_ID (the engine's
// JwtValidation.cs requires this — see Program.cs:120-138). To get a token
// with that audience, the SPA must request a custom API scope exposed by
// the App Reg itself: `api://<clientId>/access_as_user`. Requesting only
// OIDC scopes (`openid profile email`) without an API scope causes MSAL to
// fall back to the App Reg's pre-configured Graph permissions, producing a
// token with aud=00000003-... (Microsoft Graph) which the engine rejects.
//
// Prerequisite: in Azure portal → App Reg → Expose an API, set the
// Application ID URI (default: api://<clientId>) and add an `access_as_user`
// scope. See docs/runbooks/OPERATOR-ONBOARDING.md for setup details.
let cachedScopes: string[] | null = null;
export function getScopes(): string[] {
  if (cachedScopes) return cachedScopes;
  const { clientId } = readEnv();
  cachedScopes = [`api://${clientId}/access_as_user`];
  return cachedScopes;
}

// Backwards-compatible Proxy: existing call sites that import `SCOPES` and
// pass it as `scopes: SCOPES` still work — array index access lazy-resolves.
export const SCOPES: string[] = new Proxy([] as string[], {
  get(_t, prop) {
    const real = getScopes();
    const value = (real as unknown as Record<string, unknown>)[prop as string];
    return typeof value === "function" ? (value as (...a: unknown[]) => unknown).bind(real) : value;
  },
  has(_t, prop) {
    return prop in getScopes();
  },
  ownKeys() {
    return Reflect.ownKeys(getScopes());
  },
  getOwnPropertyDescriptor(_t, prop) {
    return Object.getOwnPropertyDescriptor(getScopes(), prop);
  },
});

let cachedInstance: PublicClientApplication | null = null;

function readEnv(): { clientId: string; tenantId: string } {
  const clientId = import.meta.env.VITE_OAUTH_CLIENT_ID as string | undefined;
  const tenantId = import.meta.env.VITE_OAUTH_TENANT_ID as string | undefined;
  if (!clientId || !tenantId) {
    throw new Error(
      "operator-console: VITE_OAUTH_CLIENT_ID and VITE_OAUTH_TENANT_ID must be set at build time. " +
        "See docs/runbooks/OPERATOR-ONBOARDING.md.",
    );
  }
  return { clientId, tenantId };
}

/** Lazy-instantiated MSAL singleton. First call validates env + builds the
 *  PublicClientApplication; subsequent calls return the cached instance. */
export function getMsalInstance(): PublicClientApplication {
  if (cachedInstance) return cachedInstance;
  const { clientId, tenantId } = readEnv();
  const config: Configuration = {
    auth: {
      clientId,
      authority: `https://login.microsoftonline.com/${tenantId}/v2.0`,
      redirectUri: `${window.location.origin}/auth/callback`,
    },
    cache: {
      // AUTH-02 — localStorage keyed by user identity; no third-party cookies.
      cacheLocation: "localStorage",
      storeAuthStateInCookie: false,
    },
  };
  cachedInstance = new PublicClientApplication(config);
  return cachedInstance;
}

/**
 * Convenience accessor used by AuthGate / AuthCallback / fetcher. Existing
 * call sites that imported `msalInstance` directly should switch to
 * `getMsalInstance()`. We also export `msalInstance` as a getter-style
 * binding so call sites that read `msalInstance.foo` keep working —
 * the proxy lazy-resolves on every property access.
 */
export const msalInstance: PublicClientApplication = new Proxy({} as PublicClientApplication, {
  get(_target, prop) {
    const real = getMsalInstance();
    const value = (real as unknown as Record<string, unknown>)[prop as string];
    return typeof value === "function" ? (value as (...args: unknown[]) => unknown).bind(real) : value;
  },
});

/** Helper for fetcher.ts — returns the active account or null. */
export function getActiveAccount(): AccountInfo | null {
  const instance = getMsalInstance();
  const active = instance.getActiveAccount();
  if (active) return active;
  const all = instance.getAllAccounts();
  if (all.length > 0 && all[0]) {
    instance.setActiveAccount(all[0]);
    return all[0];
  }
  return null;
}
